import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Grid, Group, Title, Modal, Stack, Text, useMantineTheme, Drawer, Collapse } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import { VendorList } from "../../Components/Vendors/VendorList";
import { VendorDetailView } from "../../Components/Vendors/VendorDetailView";
import VendorEditView from "../../Components/Vendors/VendorEditView";
import { ListVendorDto, MutableVendorDto, VendorDto } from "../../models/vendors.models";
import { useCreateVendorMutation, useDeleteVendorMutation, useGetVendorByIdQuery, useGetVendorsQuery, useUpdateVendorMutation } from "../../api/vendor.api";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";

const VendorsPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ vendorId?: string }>();
	const isEditing = useMatch("/vendors/:vendorId/edit") !== null;
	const isNew = params.vendorId === "new";
	const vendorIdFromParams = params.vendorId || null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const { data: vendors, isPending: isLoadingVendors } = useGetVendorsQuery();
	const [selectedVendor, setSelectedVendor] = useState<ListVendorDto | null>(null);
	const { data: vendor, isLoading } = useGetVendorByIdQuery(selectedVendor?.VendorId || vendorIdFromParams || null);

	const [discardChangesModalOpened, { open: openDiscardChangesModal, close: closeDiscardChangesModal }] = useDisclosure(false);
	const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isChanged, setIsChanged] = useState(false);
	const [requestedVendorId, setRequestedVendorId] = useState<string | null>(null);

	const { mutate: createVendor, isPending: isCreating } = useCreateVendorMutation();
	const { mutate: updateVendor, isPending: isUpdating } = useUpdateVendorMutation();
	const { mutate: deleteVendor } = useDeleteVendorMutation();

	const isSaving = isCreating || isUpdating;

	useEffect(() => {
		if (vendorIdFromParams) {
			const selected = vendors?.find((item: ListVendorDto) => item.VendorId === vendorIdFromParams);
			if (selected) {
				setSelectedVendor?.(selected);
				if (isMobile) {
					openDetailViewDrawer();
				}
			}
		} else if (vendors && vendors.length > 0) {
			setSelectedVendor?.(vendors[0]);
		}
	}, [vendorIdFromParams, vendors, isMobile]);

	useEffect(() => {
		if (!vendorIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [location.pathname]);

	const handleEdit = () => {
		navigate(`/vendors/${vendor?.VendorId}/edit`);
	};

	const handleNew = () => {
		setIsChanged(false);
		setSelectedVendor(null);
		navigate(`/vendors/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleVendorSelect = (selected: ListVendorDto | null) => {
		if (!selected) return;

		setRequestedVendorId(selected.VendorId);

		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
			return;
		}

		setSelectedVendor(selected);
		navigate(`/vendors/${selected.VendorId}`);
		setIsChanged(false);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSave = (values: MutableVendorDto) => {
		if (isNew) {
			createVendor(values, {
				onSuccess: (response) => {
					setIsChanged(false);
					navigate(`/vendors/${response.data.VendorId}`, { replace: true });
				}
			});
		} else {
			updateVendor(values, {
				onSuccess: () => {
					setIsChanged(false);
					navigate(-1);
				}
			});
		}
	};

	const handleDelete = () => {
		if (!vendor) return;

		closeDeleteModal();
		deleteVendor(vendor, {
			onSuccess: () => {
				setSelectedVendor(null);
				navigate(`/vendors`, { replace: true });
			}
		});
	};

	const handleClose = () => {
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
		} else {
			closeDiscardChangesModal();
			setIsChanged(false);
			navigate(-1);

			if (isMobile && isNew) {
				closeDetailViewDrawer();
			}
		}
	};

	const handleChanged = (changed: boolean) => {
		setIsChanged(changed);
	};

	const handleDiscardChanges = () => {
		closeDiscardChangesModal();
		setIsChanged(false);

		if (isMobile && isNew) {
			closeDetailViewDrawer();
		}

		if (requestedVendorId && requestedVendorId !== vendor?.VendorId) {
			navigate(`/vendors/${requestedVendorId}`);
			setRequestedVendorId(null);
		} else {
			navigate(-1);
		}
	};

	const handleCancelDiscard = () => {
		closeDiscardChangesModal();
	};

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	};

	return <>
		<Modal opened={discardChangesModalOpened} onClose={handleCancelDiscard} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>If you leave now, you’ll lose unsaved changes to this vendor.</Text>
				<Group justify="right">
					<Button variant="outline" onClick={handleCancelDiscard} radius="lg">Cancel</Button>
					<Button variant="filled" onClick={handleDiscardChanges} radius="lg">Discard Changes</Button>
				</Group>
			</Stack>
		</Modal>

		<Modal opened={deleteModalOpened} onClose={closeDeleteModal} title={<Text fz="xl" fw="bold">Delete Vendor</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to delete the vendor:</Text>
				<Text fw="bold">{vendor?.Name}</Text>
				<Group justify="right">
					<Button variant="outline" onClick={closeDeleteModal} radius="lg">Cancel</Button>
					<Button color="red" onClick={handleDelete} radius="lg">Delete</Button>
				</Group>
			</Stack>
		</Modal>

		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{isEditing || isNew ? (
				<VendorEditView vendor={!isNew ? vendor : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
			) : (
				<VendorDetailView vendor={vendor} isLoading={isLoading} onEdit={handleEdit} onClose={handleDetailViewDrawerClose} onDelete={openDeleteModal} />
			)}
		</Drawer>

		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}
		</Collapse>

		<Group justify="space-between">
			<Title order={2}>Vendors</Title>
			<RoleTemplate role={AzureADRoles.Admin}>
				<Button radius="xl" leftSection={<IconPlus />} onClick={handleNew}>New Vendor</Button>
			</RoleTemplate>
		</Group>

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!vendors || vendors.length === 0) && !isNew && !isEditing) ? 12 : 5 }}>
				<VendorList vendors={vendors} isLoading={isLoadingVendors} selectedVendor={selectedVendor} setSelectedVendor={handleVendorSelect} />
			</Grid.Col>
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{isEditing || isNew ? (
					<VendorEditView vendor={!isNew ? vendor : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
				) : vendors && vendors.length > 0 ? (
					<VendorDetailView vendor={vendor} isLoading={isLoading} onEdit={handleEdit} onDelete={openDeleteModal} />
				) : <></>}
			</Grid.Col>
		</Grid>
	</>;
};

export default VendorsPage;
